<template>
  <div class="section-selector-container h-full" />
  <NModal :auto-focus="false" close-on-esc :on-esc="cancel" show to=".section-selector-container">
    <NCard
      :bordered="false"
      class="section-selector__card rounded-2 h-full"
      :content-style="{ overflowY: 'auto', backgroundColor: themeVars.primary6, borderRadius: '8px' }"
    >
      <NGrid class="h-full" cols="3" item-responsive responsive="screen">
        <NGridItem class="pa-2" span="3 m:2">
          <div ref="toolbarWrapper" class="sticky z-100">
            <CSearchFilters
              v-model:filters="filters"
              :disabled="!!isFetchingSections"
              entity="canvas"
              is-section
              :placeholder="t('canvasUI.canvasBuilder.sectionSelector.searchByNameOrTag')"
              :search-in-options="isSearchInEnabled ? searchInOptions : undefined"
              v-bind="popularityProps"
              @update:filters="fetchSectionsAfterFiltering"
              @update:search="handleSearch"
              @update:searchIn="handleUpdateSearchIn"
            />

            <SectionSelectorToolbar
              :are-all-selected="areAllSelected"
              :are-some-selected="areSomeSelected"
              :current-page="currentPage"
              :disable-pagination="enablePipeMultisearch"
              :first-page-total-displayed-items="firstPageTotalDisplayedSections ?? 0"
              :loading="isFetchingSections"
              :page-size="pageSize"
              :total-items="availableSectionsTotalCount"
              :total-selected="selectedSections?.length ?? 0"
              @page-first="fetchFirstChunk"
              @page-last="fetchLastChunk"
              @page-next="fetchNextChunk"
              @page-previous="fetchPreviousChunk"
              @toggle-select-all="toggleSelectAll"
            />
          </div>

          <div v-if="isFetchingSections" class="pt-3 overflow-auto" :style="{ height: wrapperHeight }">
            <NSkeleton v-for="n in 8" :key="n" class="w-full rounded" :class="{ 'mt-1': n > 1 }" :height="95" />
          </div>
          <CList
            v-else
            class="rounded-2 flex bg-base overflow-y-auto"
            :data="orderedAvailableSections"
            :height="parseInt(wrapperHeight)"
            :maxRowHeight="200"
            :movable="false"
            :selectedIds="selectedSections.map((s) => s.id)"
            @item:click="onSectionSelect"
          >
            <template #item="{ item, index, isSelected }">
              <SectionSelectorItem
                :data="orderedAvailableSections"
                :filters="filters"
                :index="index"
                :is-selected="isSelected"
                :item="item"
                :selected-sections="selectedSections"
              >
                <template #actions>
                  <CButton
                    v-if="launchDarkly.enable_section_selector_edit_section && isAdmin"
                    v-tooltip="t('canvasUI.canvasBuilder.sectionSelector.openSectionInEditMode')"
                    circle
                    class="h-9 w-9 absolute right-3 opacity-0 group-hover:opacity-100 transition-opacity"
                    secondary
                    type="default"
                    @click.stop="navigateToSectionEdit(item)"
                  >
                    <template #icon>
                      <CIcon class="ml-[2px] color-primary" icon="arrow-up-right-from-square" size="12" />
                    </template>
                  </CButton>
                </template>
              </SectionSelectorItem>
            </template>
          </CList>
        </NGridItem>
        <NGridItem class="pa-2" span="3 m:1">
          <SectionSelectorSelected
            v-model="selectedSections"
            @clear="selectedSections = []"
            @remove="onRemoveSelection"
          />
        </NGridItem>
      </NGrid>

      <template #footer>
        <div class="flex gap-2 justify-end mt-2 relative top-1">
          <CButton :disabled="isSavingCanvas" @click="emit('cancel')">
            {{ cancelText || t('canvasUI.common.cancel') }}
          </CButton>
          <CButton
            :disabled="isDoneDisabled"
            :loading="isSavingCanvas"
            type="primary"
            @click="emit('select', selectedSections)"
          >
            {{ doneText || t('canvasUI.common.done') }}
          </CButton>
        </div>
      </template>
    </NCard>
  </NModal>
</template>
<script setup lang="ts">
import { orderBy, sortBy, unionBy, uniq } from 'lodash-es'
import { NModal, NCard, NGrid, NGridItem, NSkeleton } from 'naive-ui'
import { computed, inject, onMounted, ref, type ComputedRef, type Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { AxiosInstance } from 'axios'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CList from '@lib/components/CList/CList.vue'

import SectionSelectorItem from './SectionSelectorItem.vue'
import SectionSelectorToolbar from './SectionSelectorToolbar.vue'
import { fetchSectionsWithPipeMultiSearch } from './SectionSelector.helpers'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import type { SearchInOption } from '@lib/components/CSearchFilters/CSearchFilters.types'
import SectionSelectorSelected from '@canvas-builder/components/ui/SectionList/SectionSelectorSelected.vue'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import { usePopularity, EntityType } from '@canvas-builder/composables/usePopularity'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useThemeVars from '@lib/composables/useThemeVars'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { useElementHeightResize } from '@lib/composables/useElementHeightResize'
import { normalizeFilterParams } from '@lib/components/filters/filter.util'
import type { CanvasBuilderProps, SectionListProps } from '@canvas-builder/types/canvas'
import type { CanvasRetrieve, Canvas } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { InstanceSettings } from '@lib/types/instanceSettings.types'
import type { OrganizationSettings } from '@lib/types/organizationSettings.types'
import type { PitcherInfo } from '@lib/types/app'

const props = defineProps<{
  isSavingCanvas: boolean
  selectedSectionIds: CanvasRetrieve['id'][]
  selectionStrategy?: SectionListProps['selection_strategy']
  cancelText?: string
  doneText?: string
}>()

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'select', items: CanvasRetrieve[]): void
}>()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)
const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)
const openSection = inject<{
  (canvasId: CanvasRetrieve['id'], { editMode }: { editMode?: boolean }): void
}>('open-section')
const restApiAxios = inject<Ref<AxiosInstance | undefined>>('restApiAxios')
const pageSize = computed(() => launchDarkly.value.canvas_section_selector_page_size || 500)
const filters = ref<Record<string, any>>({})

const { engagingSections, getOrderingConfiguration, getSystemFilterSectionIds, popularSections, getDefaultOrdering } =
  useSectionPopularity(computed(() => filters.value))
const { fetchPopularityData } = usePopularity()
const { t } = useI18n()
const listCanvases = inject<CanvasBuilderProps['listCanvases']>('listCanvases')
const search = ref('')
const availableSections = ref<CanvasRetrieve[]>([])
const availableSectionsTotalCount = ref(0)
const selectedSections = ref<CanvasRetrieve[]>([])
const isFetchingSections = ref(false)
const isFetchingSelectedSections = ref(false)
const currentPage = ref(1)
const firstPageTotalDisplayedSections = ref<number | undefined>()
const toolbarWrapper = ref<HTMLElement | null>(null)
const { wrapperHeight } = useElementHeightResize(toolbarWrapper, calculateWrapperHeight)
const { recommendedSectionIds } = useRecommendations()
const { isAdmin } = useCanvas()
const themeVars = useThemeVars()

const isSearchInEnabled = computed(() => launchDarkly.value.enable_insearch_in_section_selector)
const searchInOptions: SearchInOption[] = [
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInAll'),
    value: 'name,tags,metadata,text',
    default: true,
  },
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInFileName'),
    value: 'name',
  },
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInTags'),
    value: 'tags',
  },
  {
    label: t('canvasUI.canvasBuilder.sectionSelector.searchInMetadata'),
    value: 'metadata',
  },
]
let searchIn = isSearchInEnabled.value ? searchInOptions[0].value : undefined

const popularityProps = computed(() => ({
  engagingIds: launchDarkly.value.show_popularity_icons ? engagingSections.value.map((item) => item.id) : undefined,
  popularIds: launchDarkly.value.show_popularity_icons ? popularSections.value.map((item) => item.id) : undefined,
  recommendedIds: recommendedSectionIds.value ?? [],
  showLatestFilter: true,
}))
const isDoneDisabled = computed(() => {
  if (pitcherInfo.value?.platform === 'admin') return false

  switch (props.selectionStrategy) {
    case 'single':
      return selectedSections.value.length !== 1
    case 'at-least-one':
      return selectedSections.value.length < 1
    case 'app':
    default:
      return false
  }
})

const enablePipeMultisearch = computed(() => !!(launchDarkly.value.enable_pipe_character_multi_search && search.value))
const pipeSearchTerms = computed(() =>
  uniq(
    search.value
      .split(pitcherSettings.value?.pipe_multi_search_separator || '|')
      .map((term) => term.trim())
      .filter((term) => term),
  ),
)
const isPipeSearching = computed(() => pipeSearchTerms.value.length > 1)
const orderedAvailableSections = computed(() => {
  if (enablePipeMultisearch.value && isPipeSearching.value) {
    return availableSections.value
  }

  const sorters = getOrderingConfiguration({ sortByMetadata: true })

  return orderBy(
    availableSections.value,
    sorters.map(([sorter]) => sorter),
    sorters.map(([, order]) => order),
  )
})

const areAllSelected = computed(() =>
  availableSections.value.every((section) => selectedSections.value.some((s) => s.id === section.id)),
)
const areSomeSelected = computed(
  () =>
    availableSections.value.some((section) => selectedSections.value.some((s) => s.id === section.id)) &&
    !areAllSelected.value,
)

function navigateToSectionEdit(item: { id: string | number; [key: string]: any }) {
  openSection?.(item.id.toString(), { editMode: true })
}

function calculateWrapperHeight(height: number) {
  return `${(toolbarWrapper.value?.parentElement?.parentElement?.offsetHeight || 0) - (height || 0) - 20}px`
}

function onSectionSelect(section: CanvasRetrieve) {
  const elementExists = selectedSections.value.find((s) => s.id === section.id)
  if (elementExists) selectedSections.value = selectedSections.value.filter((s) => s.id !== section.id)
  else selectedSections.value.push(section)
}

function onRemoveSelection(section: Canvas) {
  selectedSections.value.splice(
    selectedSections.value.findIndex((s) => s.id === section.id),
    1,
  )
}

function selectChunk() {
  selectedSections.value = [
    ...selectedSections.value,
    ...orderedAvailableSections.value.filter(
      (s) => !selectedSections.value.some((selectedSection) => selectedSection.id === s.id),
    ),
  ]
}

async function fetchSections() {
  const ordering = getDefaultOrdering()
  if (enablePipeMultisearch.value) {
    try {
      isFetchingSections.value = true
      const sectionIds = getSystemFilterSectionIds()

      const result = await fetchSectionsWithPipeMultiSearch({
        listCanvases,
        search: search.value,
        pipeSearchTerms: pipeSearchTerms.value,
        isPipeSearching: isPipeSearching.value,
        pageSize: pageSize.value,
        currentPage: currentPage.value,
        filters: filters.value,
        sectionIds,
        pitcherSettings: pitcherSettings.value,
        searchIn: searchIn,
        restApiAxios: restApiAxios?.value,
      })

      availableSections.value = result.availableSections
      availableSectionsTotalCount.value = result.availableSectionsTotalCount
      firstPageTotalDisplayedSections.value = result.firstPageTotalDisplayedSections
    } catch (e) {
      console.error('Failed to fetch sections', e)
    } finally {
      isFetchingSections.value = false
    }
    return
  }

  if (!listCanvases) return

  isFetchingSections.value = true
  try {
    availableSections.value = []

    const sectionIds = getSystemFilterSectionIds()

    // only check for null value not empty array
    if (!sectionIds) {
      availableSectionsTotalCount.value = 0
      isFetchingSections.value = false
      return
    }

    const requestPromises = [
      listCanvases({
        is_section: true,
        is_template: false,
        is_draft: false,
        ordering,
        page_size: pageSize.value,
        search: search.value,
        page: currentPage.value,
        filters: normalizeFilterParams(filters.value),
        id__in: sectionIds?.length ? sectionIds.slice(0, pageSize.value).join(',') : undefined,
        search_in: searchIn,
      }),
    ]

    // only show all recommended sections if on the first page
    if (recommendedSectionIds.value?.length && currentPage.value === 1 && !sectionIds?.length)
      requestPromises.push(
        listCanvases({
          is_section: true,
          is_template: false,
          is_draft: false,
          ordering,
          search: search.value,
          page_size: pageSize.value,
          filters: normalizeFilterParams(filters.value),
          id__in: recommendedSectionIds.value.join(','),
          search_in: searchIn,
        }),
      )

    const [sectionsResponse, recommendedSectionsResponse] = await Promise.all(requestPromises)

    if (recommendedSectionsResponse && recommendedSectionsResponse.results?.length) {
      availableSectionsTotalCount.value = sectionsResponse.count

      const sectionsWithAllRecommendedSections = unionBy(
        sectionsResponse.results,
        recommendedSectionsResponse.results,
        'id',
      )
      firstPageTotalDisplayedSections.value = sectionsWithAllRecommendedSections.length
      availableSections.value = sectionsWithAllRecommendedSections
    } else {
      firstPageTotalDisplayedSections.value = undefined
      availableSectionsTotalCount.value = sectionsResponse.count
      availableSections.value = sectionsResponse.results
    }
  } catch (e) {
    console.error('Failed to fetch sections', e)
  } finally {
    isFetchingSections.value = false
  }
}

async function fetchSelectedSections() {
  if (!listCanvases) return

  const ordering = getDefaultOrdering()
  isFetchingSelectedSections.value = true
  try {
    // Split the selectedSectionIds into chunks safe enough for GET URLs
    const maxIdsPerChunk = 100 // Adjust the chunk size to be safe for GET requests
    const idChunks = []
    for (let i = 0; i < props.selectedSectionIds.length; i += maxIdsPerChunk) {
      idChunks.push(props.selectedSectionIds.slice(i, i + maxIdsPerChunk))
    }

    // Fetch sections for each chunk and combine the results
    const sectionsPromises = idChunks.map((chunk) =>
      listCanvases({
        is_section: true,
        is_template: false,
        ordering,
        page_size: pageSize.value,
        id__in: chunk.join(','),
        search_in: searchIn,
      }),
    )
    const sectionsResults = await Promise.all(sectionsPromises)
    const flattenResults = sectionsResults.flatMap((res) => res.results)
    selectedSections.value = sortBy(flattenResults, (value) => props.selectedSectionIds.indexOf(value.id))
  } catch (e) {
    console.error('Failed to fetch sections', e)
  } finally {
    isFetchingSelectedSections.value = false
  }
}

function fetchNextChunk() {
  currentPage.value++
  fetchSections()
}

function fetchPreviousChunk() {
  if (currentPage.value > 1) {
    currentPage.value--
    fetchSections()
  }
}

function fetchFirstChunk() {
  currentPage.value = 1
  fetchSections()
}

function fetchLastChunk() {
  if (availableSectionsTotalCount.value > 0) {
    currentPage.value = Math.ceil(availableSectionsTotalCount.value / pageSize.value)
    fetchSections()
  }
}

function fetchSectionsAfterFiltering() {
  currentPage.value = 1
  fetchSections()
}

function handleSearch(newValue: string) {
  search.value = newValue
  currentPage.value = 1
  fetchSections()
}

function handleUpdateSearchIn(newValue: string) {
  searchIn = newValue
  if (search.value) {
    currentPage.value = 1
    fetchSections()
  }
}

function toggleSelectAll() {
  if (areAllSelected.value)
    selectedSections.value = selectedSections.value.filter(
      (s) => !availableSections.value.some((section) => s.id === section.id),
    )
  else selectChunk()
}

onMounted(() => {
  if (launchDarkly.value.show_popularity_icons) {
    fetchPopularityData(EntityType.SECTION)
  }
  fetchSections()
  fetchSelectedSections()
})

const cancel = () => {
  emit('cancel')
}
</script>

<style scoped lang="scss">
.section-selector-container {
  font-family: var(--font-family);

  :deep(.n-modal-scroll-content) {
    height: 100%;
    padding: 24px;
  }
}
</style>
